import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Plus,
  Wrench,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Download,
  Trash2
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fmtTz } from "../lib/time";

const URGENCY_LEVELS = [
  { value: 'low', label: 'Low', color: 'bg-blue-100 text-blue-700' },
  { value: 'medium', label: 'Medium', color: 'bg-amber-100 text-amber-700' },
  { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-700' },
  { value: 'critical', label: 'Critical', color: 'bg-rose-100 text-rose-700' }
];

export default function AdminServiceRequests() {
  const { authAxios, user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  const [processingId, setProcessingId] = useState(null);

  const isAdmin = user?.role === 'admin';

  const [formData, setFormData] = useState({
    equipment_id: '',
    description: '',
    urgency: 'medium',
    preferred_resolution_date: '',
    project_id: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [reqRes, equipRes, projRes] = await Promise.all([
        authAxios.get('/forms/service-requests'),
        authAxios.get('/equipment'),
        authAxios.get('/projects')
      ]);
      setRequests(reqRes.data);
      setEquipment(equipRes.data);
      setProjects(projRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setFormData({
      equipment_id: '',
      description: '',
      urgency: 'medium',
      preferred_resolution_date: '',
      project_id: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.equipment_id || !formData.description) {
      toast.error('Please fill required fields');
      return;
    }

    setSubmitting(true);
    try {
      await authAxios.post('/forms/service-requests', {
        ...formData,
        preferred_resolution_date: formData.preferred_resolution_date || null,
        project_id: formData.project_id && formData.project_id !== 'none' ? formData.project_id : null
      });
      toast.success('Service request submitted');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAction = async (requestId, action) => {
    setProcessingId(requestId);
    try {
      await authAxios.post(`/forms/service-requests/${requestId}/action?action=${action}`);
      toast.success(`Request ${action}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process request');
    } finally {
      setProcessingId(null);
    }
  };

  const exportPDF = async (requestId) => {
    try {
      const response = await authAxios.get(`/pdf/service-request/${requestId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `service_request_${requestId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this service request permanently?')) return;
    try {
      await authAxios.delete(`/forms/service-requests/${id}`);
      toast.success('Service request deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700',
      approved: 'bg-emerald-100 text-emerald-700',
      in_progress: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700',
      denied: 'bg-rose-100 text-rose-700'
    };
    return (
      <Badge className={styles[status]}>
        {status?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
      </Badge>
    );
  };

  const getUrgencyBadge = (urgency) => {
    const level = URGENCY_LEVELS.find(u => u.value === urgency);
    return <Badge className={level?.color || 'bg-stone-100 text-stone-700'}>{urgency?.toUpperCase()}</Badge>;
  };

  const filteredRequests = requests.filter(r => {
    if (activeTab === 'all') return true;
    return r.status === activeTab;
  });

  const counts = {
    all: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    approved: requests.filter(r => r.status === 'approved').length,
    in_progress: requests.filter(r => r.status === 'in_progress').length,
    completed: requests.filter(r => r.status === 'completed').length
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="service-requests">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-stone-900 font-heading">Service Requests</h1>
          <p className="text-stone-500 mt-1">Request equipment service and repairs</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="new-request-btn">
              <Plus className="w-5 h-5 mr-2" />
              New Service Request
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wrench className="w-5 h-5 text-emerald-600" />
                New Service Request
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Equipment *</Label>
                <Select value={formData.equipment_id} onValueChange={(v) => setFormData({ ...formData, equipment_id: v })}>
                  <SelectTrigger data-testid="service-equipment-select">
                    <SelectValue placeholder="Select equipment" />
                  </SelectTrigger>
                  <SelectContent>
                    {equipment.map(e => (
                      <SelectItem key={e.id} value={e.id}>{e.name} ({e.equipment_id})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Urgency *</Label>
                <Select value={formData.urgency} onValueChange={(v) => setFormData({ ...formData, urgency: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {URGENCY_LEVELS.map(u => (
                      <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Description *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the issue or service needed..."
                  rows={4}
                  required
                  data-testid="service-description"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Preferred Resolution Date</Label>
                  <Input
                    type="date"
                    value={formData.preferred_resolution_date}
                    onChange={(e) => setFormData({ ...formData, preferred_resolution_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Project (Optional)</Label>
                  <Select value={formData.project_id} onValueChange={(v) => setFormData({ ...formData, project_id: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select project" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Project</SelectItem>
                      {projects.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <DialogFooter>
                <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-500">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Submit Request
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-6 bg-stone-100 flex-wrap">
          <TabsTrigger value="all" className="data-[state=active]:bg-white">All ({counts.all})</TabsTrigger>
          <TabsTrigger value="pending" className="data-[state=active]:bg-white">Pending ({counts.pending})</TabsTrigger>
          <TabsTrigger value="approved" className="data-[state=active]:bg-white">Approved ({counts.approved})</TabsTrigger>
          <TabsTrigger value="in_progress" className="data-[state=active]:bg-white">In Progress ({counts.in_progress})</TabsTrigger>
          <TabsTrigger value="completed" className="data-[state=active]:bg-white">Completed ({counts.completed})</TabsTrigger>
        </TabsList>

        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Wrench className="w-5 h-5 text-stone-400" />
              Service Requests
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
              </div>
            ) : filteredRequests.length === 0 ? (
              <div className="text-center py-12 text-stone-500">
                <Wrench className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No service requests found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-stone-100">
                      <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Equipment</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Urgency</TableHead>
                      <TableHead className="hidden md:table-cell text-xs uppercase tracking-wider text-stone-500 font-semibold">Description</TableHead>
                      <TableHead className="hidden md:table-cell text-xs uppercase tracking-wider text-stone-500 font-semibold">Requested By</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Status</TableHead>
                      <TableHead className="hidden md:table-cell text-xs uppercase tracking-wider text-stone-500 font-semibold">Date</TableHead>
                      <TableHead className="sticky right-0 bg-white text-xs uppercase tracking-wider text-stone-500 font-semibold text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRequests.map((req) => (
                      <TableRow key={req.id} className="border-stone-100 hover:bg-stone-50">
                        <TableCell className="font-medium">{req.equipment_name}</TableCell>
                        <TableCell>{getUrgencyBadge(req.urgency)}</TableCell>
                        <TableCell className="hidden md:table-cell max-w-[200px] truncate text-stone-600">{req.description}</TableCell>
                        <TableCell className="hidden md:table-cell text-stone-600">{req.requested_by_name}</TableCell>
                        <TableCell>{getStatusBadge(req.status)}</TableCell>
                        <TableCell className="hidden md:table-cell text-stone-600">{fmtTz(req.created_at, 'MMM d, yyyy')}</TableCell>
                        <TableCell className="sticky right-0 bg-white">
                          <div className="flex items-center gap-1 justify-end">
                            {isAdmin && req.status === 'pending' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleAction(req.id, 'approved')}
                                  disabled={processingId === req.id}
                                  className="text-emerald-600 hover:bg-emerald-50"
                                >
                                  {processingId === req.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleAction(req.id, 'denied')}
                                  disabled={processingId === req.id}
                                  className="text-rose-600 hover:bg-rose-50"
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            {isAdmin && req.status === 'approved' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleAction(req.id, 'in_progress')}
                                disabled={processingId === req.id}
                                className="text-blue-600 hover:bg-blue-50"
                              >
                                Start
                              </Button>
                            )}
                            {isAdmin && req.status === 'in_progress' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleAction(req.id, 'completed')}
                                disabled={processingId === req.id}
                                className="text-green-600 hover:bg-green-50"
                              >
                                Complete
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => exportPDF(req.id)}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            {isAdmin && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(req.id)}
                                data-testid={`delete-service-${req.id}`}
                              >
                                <Trash2 className="w-4 h-4 text-rose-600" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </Tabs>
    </div>
  );
}
